import { useCallback } from "react";
import { telemetryPath } from "@ramirotw/telemetry";
import { captureClientError } from "@/lib/telemetry";

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function useApi() {
  const authFetch = useCallback(async (path: string, options: RequestInit = {}) => {
    const baseUrl = import.meta.env.VITE_API_URL || "";

    let response: Response;

    try {
      response = await fetch(`${baseUrl}${path}`, {
        ...options,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });
    } catch (error) {
      captureClientError(error, "api_network_error", {
        route: telemetryPath(path),
        method: options.method || "GET",
      });
      throw error;
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error = new ApiError(
        body.error || `API error: ${response.status}`,
        response.status,
        body.code
      );

      if (response.status >= 500) {
        captureClientError(error, "api_server_error", {
          route: telemetryPath(path),
          method: options.method || "GET",
          status: response.status,
        });
      }

      throw error;
    }

    return response.json();
  }, []);

  return { authFetch };
}
